import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "lib/store";
import { MessageType } from "./types";

interface MessageState {
  currentChatType: MessageType;
  referreDetails: {
    name: string;
    image: string;
  };
  customerDetails: {
    customer_name: string;
    additional_comments: string;
    referee_name: string;
    referee_avatar: string;
  };
  currentChannelUrl: string;
  isExistingBookingLink: boolean;
}

const initialState: MessageState = {
  currentChatType: MessageType.Referees,
  referreDetails: {
    name: "",
    image: "",
  },
  customerDetails: {
    customer_name: "",
    additional_comments: "",
    referee_name: "",
    referee_avatar: "",
  },
  currentChannelUrl: "",
  isExistingBookingLink: false,
};

// Slice
export const messageSlice = createSlice({
  name: "message",
  initialState,
  reducers: {
    setCurrentChatType(
      state: MessageState,
      action: PayloadAction<MessageType>
    ) {
      state.currentChatType = action.payload;
    },
    setCustomerDetails(state, action) {
      state.customerDetails = action.payload;
    },
    resetCustomerDetails(state) {
      state.customerDetails = {
        customer_name: "",
        additional_comments: "",
        referee_name: "",
        referee_avatar: "",
      };
    },
    setReferreDetails(state, action) {
      state.referreDetails = action.payload;
    },
    resetReferreDetails(state) {
      state.referreDetails = {
        name: "",
        image: "",
      };
    },
    setCurrentChannelUrl(state, action) {
      state.currentChannelUrl = action.payload;
    },
    resetCurrentChannelUrl(state) {
      state.currentChannelUrl = "";
    },
    setIsExistingBookingLink(state, action) {
      state.isExistingBookingLink = action.payload;
    },
    resetIsExistingBookingLink(state) {
      state.isExistingBookingLink = false;
    },
  },
});

// Selector to access the message slice state
const selectSlice = (state: RootState) => state.messageSlice || initialState;

export const selectCurrentChatType = createSelector(
  [selectSlice],
  (state: MessageState) => state.currentChatType
);

export const selectCustomerDetails = createSelector(
  [selectSlice],
  (state: MessageState) => state.customerDetails
);

export const selectReferreDetails = createSelector(
  [selectSlice],
  (state: MessageState) => state.referreDetails
);

export const selectCurrentChannelUrl = createSelector(
  [selectSlice],
  (state: MessageState) => state.currentChannelUrl
);

export const selectIsExistingBookingLink = createSelector(
  [selectSlice],
  (state: MessageState) => state.isExistingBookingLink
);

export const {
  setCurrentChatType,
  setCustomerDetails,
  resetCustomerDetails,
  setReferreDetails,
  resetReferreDetails,
  setCurrentChannelUrl,
  resetCurrentChannelUrl,
  setIsExistingBookingLink,
  resetIsExistingBookingLink,
} = messageSlice.actions;

export default messageSlice.reducer;
